import {
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    EmptyState,
    ListItemElement,
    RowAnchorModel,
    SkeletonLoaderModel,
    StandardListPageModel,
    Thumbnail
} from '@symplicity/syng';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
    EventsResponse,
    EventsService,
    EventModel,
    AttendeeTypes,
    EventTypes,
    MESSAGES,
    EventView,
    DEFAULT_PAGE,
    DEFAULT_PER_PAGE,
    PER_PAGE_VALUES,
} from '../../services';

interface EventsItems extends EventsResponse {
    items: StandardListPageModel[];
}

@Component({
    selector: 'cf-events-list',
    templateUrl: './events-list.component.html'
})
export class EventsListComponent implements OnInit, OnDestroy {
    @Input()
    public eventView: EventView;

    public readonly rowAnchor = new RowAnchorModel({
        onClick: (event) => this.onRowAnchorClick(event.item.id),
        enable: false
    });
    public readonly skeleton = new SkeletonLoaderModel({
        thumbnails: true,
        thumbnailsStyle: 'thumbnailbox',
        texts: 4
    });
    public emptyState: EmptyState;
    public readonly items$ = new Subject<StandardListPageModel[]>();
    public readonly page$ = new Subject<number>();
    public readonly perPage$ = new Subject<number>();
    public readonly totalCount$ = new Subject<number>();
    private models: EventModel[] = [];
    private readonly destroyed$ = new Subject<void>();

    constructor(
        private eventsService: EventsService,
        private route: ActivatedRoute,
        private router: Router,
        private translate: TranslateService,
        private window: Window
    ) {}

    public ngOnInit(): void {
        const message: Partial<EmptyState> = this.eventView === 'upcoming'
            ? {
                title: 'events.No career fairs planned',
                message: 'events.When new fairs are scheduled, they will show here.'
            }
            : {
                title: 'events.No past fairs yet',
                message: `events.We didn't find any past career fairs.`
            };

        this.emptyState = new EmptyState({
            ...message,
            src: '/components/valkyrie/images/empty-calendar-gray.svg'
        });


        this.route.queryParams.pipe(
            map(params => {
                const parsedPage = parseInt(params.page, 10);
                const parsedPerPage = parseInt(params.perPage, 10);

                const page = !isNaN(parsedPage) && parsedPage > 0
                    ? parsedPage
                    : DEFAULT_PAGE;

                const perPage = !isNaN(parsedPerPage) && PER_PAGE_VALUES.includes(parsedPerPage)
                    ? parsedPerPage
                    : DEFAULT_PER_PAGE;

                return { page, perPage };
            }),
            takeUntil(this.destroyed$)
        ).subscribe(params => this.getEvents(params.page, params.perPage));
    }

    public ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    public onReload(params: any): void {
        const { page, perPage } = params;

        this.router.navigate([], {
            queryParams: { page, perPage },
            queryParamsHandling: 'merge'
        });
    }

    private getEvents(page: number, perPage: number): void {
        const params = {
            view: this.eventView,
            page,
            perPage
        };

        this.rowAnchor.enable = false;
        this.eventsService.getEvents(params).pipe(
            map(response => this.getItems(response))
        ).subscribe(
            response => {
                this.items$.next(response.items);
                this.page$.next(response.page);
                this.perPage$.next(response.perPage);
                this.totalCount$.next(response.total);
                this.rowAnchor.enable = true;
            },
            err => console.log('Error', err)
        );
    }

    private getItems(response: EventsResponse): EventsItems {
        this.models = response.models;

        const items = this.models.map(model => new StandardListPageModel({
            rowAnchor: undefined,
            id: model.event_id,
            texts: this.getEventTexts(model),
            thumbnails: new Thumbnail({
                type: model.background_image ? 'image' : 'icon',
                src: model.background_image || 'icn-calendar',
                autoColor: model.event_id,
                styleClass: 'thumbnail-mobile-avatar thumbnail-icon icn-autocolor',
                imagePosition: model.background_image_alignment || 'center center'
            }),
            ariaLabel: model.label
        }));

        return { ...response, items };
    }

    private getEventTexts(model: EventModel): ListItemElement[] {
        const texts: ListItemElement[] = [];
        const tags: string[] = [];
        const eventDateLabel = this.eventsService.getEventDateLabel('list', model);

        if (model.attendee_type._id === AttendeeTypes.Student) {
            tags.push(this.translate.instant(MESSAGES.TAGS.ATTENDEE_TYPES.STUDENT));
        }

        if (model.type._id === EventTypes.VirtualCareerFair) {
            tags.push(this.translate.instant(MESSAGES.TAGS.EVENT_TYPES.VIRTUAL_CAREER_FAIR));
        }

        texts.push(
            new ListItemElement({
                label: eventDateLabel.label,
                styleClass: 'xs-text text-bold text-uppercase space-top-xs ' + (eventDateLabel.isToday? 'text-primary' : 'secondary-text')
            }),
            new ListItemElement({
                label: model.label,
                defaultStyleType: 'title',
                styleClass: 'inline'
            })
        );

        tags.length && tags.forEach(tag => texts.push(
            new ListItemElement({
                label: tag,
                defaultStyleType: 'tag',
                styleClass: 'display-sm-none badge-with-title'
            })
        ));

        model.overview && texts.push(
            new ListItemElement({
                label: model.overview,
                defaultStyleType: 'description',
                styleClass: 'text-gray text-truncate-3 white-space-normal'
            })
        );

        tags.length && tags.forEach(tag => texts.push(
            new ListItemElement({
                label: tag,
                defaultStyleType: 'tag',
                styleClass: 'hide display-sm-inline-block space-top-sm'
            })
        ));

        return texts;
    }

    private onRowAnchorClick(eventId: string): void {
        const model = this.models.find(model => model.event_id === eventId);

        if (model.attendee_type._id !== AttendeeTypes.Student) {
            this.router.navigate(['/events', eventId], {
                relativeTo: this.route.parent
            });
        } else {
            this.window.location.href = `/events/${model.nickname}/register`;
        }
    }
}
